/**
 * Authentication wrappers for React Router 7
 * 
 * This replaces redux-auth-wrapper/history4 which is not compatible with React Router 7.
 * Uses React hooks and Navigate component for redirects.
 */
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";

/**
 * Get redirect query parameter from location
 */
function getRedirectQueryParam(location) {
  if (!location || !location.search) return null;
  const params = new URLSearchParams(location.search);
  return params.get("redirect") || params.get("next");
}

/**
 * Check if user is going to logout route
 */
function isGoingToLogoutRoute(location) {
  const redirect = getRedirectQueryParam(location);
  return redirect === "/logout";
}

/**
 * Check if multi-factor auth is required
 */
function needsMultiFactor(state, location) {
  return state.user.profile.loginResult && 
         state.user.profile.loginResult.multiFactorRequired && 
         !isGoingToLogoutRoute(location);
}

/**
 * Check if multi-factor setup is required
 */
function needsMultiFactorSetup(state, location) {
  return state.user.profile.multiFactorSetupRequired && !isGoingToLogoutRoute(location);
}

/**
 * Check if redirection is needed
 */
function needsRedirection(state, location) {
  return (state.user && state.user.redirect && state.user.redirect.returnUrl) && 
         !isGoingToLogoutRoute(location);
}

/**
 * Check if password change is required
 */
function needsPasswordChange(location) {
  return JSON.parse(sessionStorage.getItem("MustChangePassword")) && !isGoingToLogoutRoute(location);
}

/**
 * HOC that requires authentication
 * Redirects to /login if user is not authenticated
 */
export function userIsAuthenticated(WrappedComponent) {
  function AuthenticatedComponent(props) {
    const location = useLocation();
    const user = useSelector(state => state.user);
    const isAuthenticated = !isEmpty(user.profile);
    const isFetching = user.isFetching;

    // If still fetching auth status, show nothing or a loader
    if (isFetching) {
      return null; // Or return a loading spinner
    }

    // If not authenticated, redirect to login with return path
    if (!isAuthenticated) {
      const returnPath = encodeURIComponent(location.pathname + location.search);
      return <Navigate to={`/login?redirect=${returnPath}`} replace />;
    }

    // User is authenticated, render the component
    return <WrappedComponent {...props} />;
  }

  AuthenticatedComponent.displayName = `userIsAuthenticated(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  return AuthenticatedComponent;
}

/**
 * HOC that requires user to NOT be authenticated
 * Redirects based on various conditions (MFA, password change, etc.)
 */
export function userIsNotAuthenticated(WrappedComponent) {
  function NotAuthenticatedComponent(props) {
    const location = useLocation();
    const state = useSelector(state => state);
    const user = state.user;
    const isNotAuthenticated = isEmpty(user) || isEmpty(user.profile);

    // If user is authenticated, redirect based on conditions
    if (!isNotAuthenticated) {
      let redirectPath = "/";

      if (needsMultiFactorSetup(state, location)) {
        redirectPath = "/two-factor-setup";
      } else if (needsMultiFactor(state, location)) {
        redirectPath = "/two-factor-auth";
      } else if (needsPasswordChange(location)) {
        redirectPath = "/change-password-required";
      } else if (needsRedirection(state, location)) {
        redirectPath = "/redirect";
      } else {
        // Check for redirect query param
        redirectPath = getRedirectQueryParam(location) || "/";
      }

      return <Navigate to={redirectPath} replace />;
    }

    // User is not authenticated, render the component
    return <WrappedComponent {...props} />;
  }

  NotAuthenticatedComponent.displayName = `userIsNotAuthenticated(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  return NotAuthenticatedComponent;
}